import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import {
  HTTPErrorCode,
  httpStatusCodes,
  HTTPSuccessCode,
  RESTApiErrorCode,
  RESTApiErrorField,
} from '../../../shared/constants/HttpStatusCodes';
import { AuthService } from '../../auth/services/authentication.service';
import { HttpStateService } from '../backend/httpstate.service';
import { HttpProgressState } from '../base/base.service';
import { MessageService, TOASTTYPE } from '../message.service';

export const apiInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const translate = inject(TranslateService);
  const messageService = inject(MessageService);
  const httpStateService = inject(HttpStateService);
  const authService = inject(AuthService);

  const parseRequest = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const updateOpts: Record<string, unknown> = {};
    switch (request.method) {
      case 'POST':
      case 'PUT':
      case 'PATCH':
        if (request.body === null) {
          break;
        }
        updateOpts['body'] = request.body;
        break;
    }
    return request.clone(updateOpts);
  };

  const handleResponse = (originalReq: HttpRequest<unknown>, options: { showMessage: boolean }) => {
    return (event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse) {
        if (options && options.showMessage) {
          const successObject = httpStatusCodes.success[event.status.toString() as unknown as HTTPSuccessCode];
          translate.get(successObject.message).subscribe((res: string) => {
            if (successObject.showToast) {
              messageService.showToast(
                TOASTTYPE.SUCCESS,
                res,
                (successObject as { displayTime?: number }).displayTime ?? undefined,
              );
            }
          });
        }
      }
    };
  };

  const handleErrorResponse = (
    originalNext: HttpHandlerFn,
    originalReq: HttpRequest<unknown>,
    options: { showMessage: boolean },
  ) => {
    return (error: Error, caught: Observable<HttpEvent<unknown>>): Observable<HttpEvent<unknown>> => {
      if (error instanceof HttpErrorResponse) {
        const errCode: string | undefined = (error.error as any)?.code;

        type ErrorObject =
          | { showToast: boolean; showModal: boolean; log: boolean; message: string; logout?: boolean }
          | { reason: string; showToast: boolean; showModal: boolean; log: boolean; message: string };

        let errorObject: ErrorObject = httpStatusCodes.error[error.status.toString() as unknown as HTTPErrorCode];

        if (errCode && httpStatusCodes.error.code[errCode as unknown as RESTApiErrorCode]) {
          errorObject = httpStatusCodes.error.code[errCode as unknown as RESTApiErrorCode];
        }

        if (
          error.status === 422 &&
          errCode === 'model_validation_error' &&
          Array.isArray((error.error as any)?.detail)
        ) {
          for (const detail of (error.error as any).detail) {
            if (detail.field && httpStatusCodes.error.field[detail.field as unknown as RESTApiErrorField]) {
              errorObject = httpStatusCodes.error.field[detail.field as unknown as RESTApiErrorField];
              break;
            }
          }
        }

        if (!(error.error instanceof Blob)) {
          if (error.status === 401 && errCode === 'invalid_token') {
            let errType: 'invalid_token' | 'expired_token' = errCode;
            const message = (error.error as any)?.message;
            if (typeof message === 'string' && message.includes('expired')) {
              errType = 'expired_token';
            }
            const refreshTokenFailed = () => {
              authService.logout({ type: errType }).subscribe();
              return throwError(() => error);
            };
            return authService.handleInvalidToken(options && options.showMessage).pipe(
              mergeMap((token) => {
                const newToken = `Bearer ${token}`;
                if (token && originalReq.headers.get('Authorization') !== newToken) {
                  const newReq = originalReq.clone({ headers: originalReq.headers.set('Authorization', newToken) });
                  return originalNext(newReq);
                } else {
                  return refreshTokenFailed();
                }
              }),
              catchError(() => {
                return refreshTokenFailed();
              }),
            );
          } else {
            if (options && options.showMessage) {
              if (errorObject.showToast) {
                translate.get(errorObject.message).subscribe((res: string) => {
                  messageService.showToast(TOASTTYPE.ERROR, res);
                });
              } else if (errorObject.log) {
                console?.error(errorObject);
              }
            }
          }
        }
        return throwError(() => error);
      }
      return caught;
    };
  };

  let parsedReq = parseRequest(req);

  const showMessage = parsedReq.headers.get('app_showMessage') !== 'false';
  parsedReq = parsedReq.clone({ headers: parsedReq.headers.delete('app_showMessage') });

  httpStateService.state.next({
    state: HttpProgressState.start,
    url: req.url,
  });

  return next(parsedReq).pipe(
    tap(handleResponse(parsedReq, { showMessage })),
    catchError(handleErrorResponse(next, parsedReq, { showMessage })),
    finalize(() => {
      setTimeout(
        () =>
          httpStateService.state.next({
            state: HttpProgressState.end,
            url: req.url,
          }),
        0,
      );
    }),
  );
};
